import { useState } from 'react';
import {
  Select, Checkbox, Button, List, Card, Typography, Spin, Alert, Avatar, Tag, Space, message,
} from 'antd';
import { ArrowLeftOutlined, CheckCircleFilled } from '@ant-design/icons';
import { useCategoryList } from 'hooks/api';
import { useToysiCategories, useToysiProducts, useToysiImportedSkus } from 'hooks/api/useToysi';
import { importFromToysi } from 'services/toysi.service';
import styles from './index.module.scss';

const ProductImg = ({ src, size = 80 }: { src: string; size?: number }) => {
  const stripped = src?.replace('enl_', '') || '';
  const [imgSrc, setImgSrc] = useState(stripped);
  const [loaded, setLoaded] = useState(false);
  return (
    <div style={{ width: size, height: size, background: '#f5f5f5', borderRadius: 6, overflow: 'hidden', flexShrink: 0, position: 'relative' }}>
      {!loaded && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          animation: 'toysi-shimmer 1.4s infinite',
        }}
        />
      )}
      <img
        src={imgSrc}
        alt=''
        loading='lazy'
        onLoad={() => setLoaded(true)}
        onError={() => { if (imgSrc !== src) setImgSrc(src); }}
        style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: loaded ? 1 : 0, transition: 'opacity 0.2s' }}
      />
      <style>{'@keyframes toysi-shimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }'}</style>
    </div>
  );
};

const { Text, Title } = Typography;
const { Option } = Select;

interface ToysiCategory {
  id: string;
  parentId?: string;
  name: string;
}

interface ToysiOffer {
  id: string;
  name: string;
  price: number;
  currencyId: string;
  pictures: string[];
  vendor?: string;
  available: boolean;
}

const ToysiImport = () => {
  const [ourCategoryId, setOurCategoryId] = useState<string | undefined>();
  const [toysiCategory, setToysiCategory] = useState<ToysiCategory | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const { data: ourCategories = [] } = useCategoryList();

  const {
    data: toysiCategories = [],
    isLoading: catsLoading,
    error: catsError,
  } = useToysiCategories();

  const {
    data: products = [],
    isLoading: prodsLoading,
    error: prodsError,
  } = useToysiProducts(toysiCategory?.id ?? null);

  const { data: importedSkus = [] } = useToysiImportedSkus();
  const importedSet = new Set(importedSkus as string[]);

  const subcategories = (toysiCategories as ToysiCategory[]).filter((c) => !!c.parentId);

  const offers = products as ToysiOffer[];
  const allSelected = offers.length > 0 && selectedIds.length === offers.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleProduct = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(offers.map((p) => p.id));
  };

  const handleBack = () => {
    setToysiCategory(null);
    setSelectedIds([]);
  };

  const handleCreate = async () => {
    if (!toysiCategory || !ourCategoryId || selectedIds.length === 0) {
      message.warning('Оберіть категорію та хоча б один продукт');
      return;
    }
    setLoading(true);
    try {
      const res = await importFromToysi({
        toysiCategoryId: toysiCategory.id,
        ourCategoryId,
        toysiProductIds: selectedIds,
      });
      const results = (res.data || []) as any[];
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const fail = results.filter((r) => r.status === 'rejected').length;
      message.success(`Створено: ${ok}, пропущено: ${fail}`);
      setSelectedIds([]);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Помилка');
    } finally {
      setLoading(false);
    }
  };

  const error = toysiCategory ? prodsError : catsError;

  return (
    <div className={styles.wrapper}>
      <Select
        placeholder='Оберіть підкатегорію з нашої БД'
        className={styles.ourCategorySelect}
        onChange={setOurCategoryId}
        value={ourCategoryId}
        showSearch
        optionFilterProp='children'
      >
        {(ourCategories as Category[]).map((cat) => (
          <Option key={cat._id} value={cat._id}>{cat.name}</Option>
        ))}
      </Select>

      <Card
        className={styles.picker}
        title={(
          <div className={styles.header}>
            {toysiCategory && (
              <Button
                icon={<ArrowLeftOutlined />}
                type='text'
                onClick={handleBack}
                className={styles.backBtn}
              />
            )}
            <Title level={5} className={styles.title}>
              {toysiCategory ? toysiCategory.name : 'Toysi — вибір підкатегорії'}
            </Title>
          </div>
        )}
      >
        {error && (
          <Alert
            type='error'
            message='Помилка завантаження'
            description={String((error as any)?.message || error)}
            showIcon
            className={styles.errorAlert}
          />
        )}

        {!toysiCategory ? (
          <Spin spinning={catsLoading}>
            <List
              className={styles.list}
              dataSource={subcategories}
              renderItem={(cat) => (
                <List.Item
                  className={styles.categoryItem}
                  actions={[
                    <Button
                      key='select'
                      type='primary'
                      size='small'
                      onClick={() => setToysiCategory(cat)}
                    >
                      Вибрати
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={(
                      <Avatar className={styles.categoryAvatar}>
                        {cat.name.charAt(0).toUpperCase()}
                      </Avatar>
                    )}
                    title={<Text className={styles.itemName}>{cat.name}</Text>}
                    description={<Text type='secondary'>{`ID: ${cat.id}`}</Text>}
                  />
                </List.Item>
              )}
              locale={{ emptyText: catsLoading ? 'Завантаження...' : 'Підкатегорії не знайдені' }}
            />
          </Spin>
        ) : (
          <Spin spinning={prodsLoading}>
            {!prodsLoading && offers.length > 0 && (
              <div className={styles.selectAllRow}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                >
                  {`Вибрати всі (${offers.length})`}
                </Checkbox>
              </div>
            )}
            <List
              className={styles.list}
              dataSource={offers}
              renderItem={(offer) => {
                const alreadyImported = importedSet.has(offer.id);
                return (
                  <List.Item
                    className={styles.productItem}
                    actions={[
                      alreadyImported
                        ? <CheckCircleFilled key='imported' style={{ color: '#52c41a', fontSize: 20 }} />
                        : (
                          <Checkbox
                            key='check'
                            checked={selectedIds.includes(offer.id)}
                            onChange={() => toggleProduct(offer.id)}
                          />
                        ),
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<ProductImg src={offer.pictures[0] || ''} />}
                      title={(
                        <Space size={6}>
                          <Text className={styles.itemName}>{offer.name}</Text>
                          {alreadyImported && (
                            <Tag color='success'>Створено</Tag>
                          )}
                        </Space>
                      )}
                      description={(
                        <Space size={4} wrap>
                          <Tag color='blue'>{`${offer.price} ${offer.currencyId}`}</Tag>
                          {offer.vendor && <Tag>{offer.vendor}</Tag>}
                          {!offer.available && <Tag color='red'>Немає в наявності</Tag>}
                        </Space>
                      )}
                    />
                  </List.Item>
                );
              }}
              locale={{ emptyText: prodsLoading ? 'Завантаження...' : 'Товари не знайдені' }}
            />
          </Spin>
        )}
      </Card>

      {toysiCategory && (
        <Button
          type='primary'
          size='large'
          loading={loading}
          disabled={!ourCategoryId || selectedIds.length === 0}
          onClick={handleCreate}
          className={styles.createBtn}
        >
          {`Створити (${selectedIds.length})`}
        </Button>
      )}
    </div>
  );
};

export default ToysiImport;
